import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Typography, Space, Alert, Spin, message } from 'antd';
import { ExclamationCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import type { ScopeVersion } from '../services/api';

const { Text } = Typography;

interface ScopeAcknowledgementProps {
  projectId: number;
  currentScopeVersion?: ScopeVersion;
  pendingAcknowledgement: boolean;
  onAcknowledged: () => void;
}

export const ScopeAcknowledgement: React.FC<ScopeAcknowledgementProps> = ({
  projectId,
  currentScopeVersion,
  pendingAcknowledgement,
  onAcknowledged,
}) => {
  const [visible, setVisible] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scopeVersion, setScopeVersion] = useState<ScopeVersion | null>(null);
  const [fetchingScope, setFetchingScope] = useState(false);

  useEffect(() => {
    if (pendingAcknowledgement && !currentScopeVersion) {
      fetchCurrentScope();
    } else if (currentScopeVersion) {
      setScopeVersion(currentScopeVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAcknowledgement, currentScopeVersion, projectId]);

  useEffect(() => {
    if (pendingAcknowledgement) {
      setVisible(true);
    }
  }, [pendingAcknowledgement]);

  const fetchCurrentScope = async () => {
    try {
      setFetchingScope(true);
      const versions = await apiService.getScopeHistory(projectId);
      const approved = versions.find(
        (v) => v.versionStatus === 'approved' && !v.acknowledgedByClient
      );
      if (approved) {
        setScopeVersion(approved);
      }
    } catch (error) {
      console.error('Failed to fetch scope version:', error);
    } finally {
      setFetchingScope(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!scopeVersion) return;

    try {
      setLoading(true);
      await apiService.acknowledgeScopeVersion(scopeVersion.id);
      message.success('Scope acknowledged successfully');
      setVisible(false);
      setConfirmed(false);
      onAcknowledged();
    } catch (error) {
      message.error('Failed to acknowledge scope');
    } finally {
      setLoading(false);
    }
  };

  if (!pendingAcknowledgement) return null;

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>Scope Acknowledgement Required</span>
        </Space>
      }
      open={visible}
      closable={false}
      maskClosable={false}
      width={800}
      footer={[
        <Button
          key="acknowledge"
          type="primary"
          onClick={handleAcknowledge}
          disabled={!confirmed || !scopeVersion}
          loading={loading}
        >
          I Acknowledge This Scope
        </Button>,
      ]}
    >
      {fetchingScope ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading scope details...</Text>
          </div>
        </div>
      ) : scopeVersion ? (
        <div>
          <Alert
            message="Please Review the Project Scope"
            description="The project scope has been updated. Please review the changes carefully and acknowledge that you have read and understood the scope before proceeding."
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />

          {scopeVersion.changeNotes && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>What Changed (Version {scopeVersion.versionNumber}):</Text>
              <div
                style={{
                  padding: 12,
                  background: '#fffbe6',
                  borderRadius: 4,
                  marginTop: 8,
                  border: '1px solid #ffe58f',
                }}
              >
                {scopeVersion.changeNotes}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 8 }}>
              <FileTextOutlined />
              <Text strong>Project Scope:</Text>
            </Space>
            <div
              style={{
                padding: 16,
                background: '#f5f5f5',
                borderRadius: 4,
                maxHeight: 300,
                overflow: 'auto',
                border: '1px solid #d9d9d9',
              }}
              dangerouslySetInnerHTML={{ __html: scopeVersion.scopeContent }}
            />
          </div>

          <div
            style={{
              padding: 16,
              background: '#fafafa',
              borderRadius: 4,
              border: '1px solid #d9d9d9',
            }}
          >
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            >
              <Text>
                I have read and understood the project scope. I confirm that this scope
                accurately reflects the agreed-upon testing boundaries and objectives.
              </Text>
            </Checkbox>
          </div>
        </div>
      ) : (
        <Alert
          message="No pending scope to acknowledge"
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

// Banner component for showing in project views
interface ScopeAcknowledgementBannerProps {
  projectId: number;
  pendingAcknowledgement: boolean;
  onAcknowledged: () => void;
}

export const ScopeAcknowledgementBanner: React.FC<ScopeAcknowledgementBannerProps> = ({
  projectId,
  pendingAcknowledgement,
  onAcknowledged,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [scopeVersion, setScopeVersion] = useState<ScopeVersion | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (pendingAcknowledgement) {
      fetchCurrentScope();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAcknowledgement, projectId]);

  const fetchCurrentScope = async () => {
    try {
      const versions = await apiService.getScopeHistory(projectId);
      const approved = versions.find(
        (v) => v.versionStatus === 'approved' && !v.acknowledgedByClient
      );
      if (approved) {
        setScopeVersion(approved);
      }
    } catch (error) {
      console.error('Failed to fetch scope version:', error);
    }
  };

  const handleAcknowledge = async () => {
    if (!scopeVersion) return;

    try {
      setLoading(true);
      await apiService.acknowledgeScopeVersion(scopeVersion.id);
      message.success('Scope acknowledged successfully');
      setModalVisible(false);
      setConfirmed(false);
      onAcknowledged();
    } catch (error) {
      message.error('Failed to acknowledge scope');
    } finally {
      setLoading(false);
    }
  };

  if (!pendingAcknowledgement) return null;

  return (
    <>
      <Alert
        message="Scope Acknowledgement Required"
        description={
          <Space direction="vertical">
            <Text>The project scope has been updated and requires your acknowledgement.</Text>
            <Button type="primary" size="small" onClick={() => setModalVisible(true)}>
              Review & Acknowledge Scope
            </Button>
          </Space>
        }
        type="warning"
        showIcon
        icon={<ExclamationCircleOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Acknowledge Project Scope</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="acknowledge"
            type="primary"
            onClick={handleAcknowledge}
            disabled={!confirmed || !scopeVersion}
            loading={loading}
          >
            I Acknowledge This Scope
          </Button>,
        ]}
      >
        {scopeVersion ? (
          <div>
            {scopeVersion.changeNotes && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>What Changed (Version {scopeVersion.versionNumber}):</Text>
                <div
                  style={{
                    padding: 12,
                    background: '#fffbe6',
                    borderRadius: 4,
                    marginTop: 8,
                    border: '1px solid #ffe58f',
                  }}
                >
                  {scopeVersion.changeNotes}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 24 }}>
              <Space style={{ marginBottom: 8 }}>
                <FileTextOutlined />
                <Text strong>Project Scope:</Text>
              </Space>
              <div
                style={{
                  padding: 16,
                  background: '#f5f5f5',
                  borderRadius: 4,
                  maxHeight: 300,
                  overflow: 'auto',
                  border: '1px solid #d9d9d9',
                }}
                dangerouslySetInnerHTML={{ __html: scopeVersion.scopeContent }}
              />
            </div>

            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            >
              <Text>
                I have read and understood the project scope and confirm it accurately
                reflects the agreed-upon testing boundaries.
              </Text>
            </Checkbox>
          </div>
        ) : (
          <Spin />
        )}
      </Modal>
    </>
  );
};
